import type { FC } from "react"
import React from "react"
import { isAddress } from "@ethersproject/address"
import { BigNumber } from "ethers"
import { Text, Stack, Flex, Button, Tag, Badge } from "@chakra-ui/react"
import isEqual from "lodash.isequal"

import NetworkTag from "common/components/NetworkTag"
import type { Vote } from "query/graphql"
import NumberTooltip from "common/components/NumberTooltip"
import UserAvatar from "common/components/UserAvatar"
import Link from "common/components/Link"
import AddressTooltip from "common/components/AddressTooltip"
import { getWeightLabel } from "common/helpers/bignumber"
import { pluralize, shortString } from "common/helpers/string"
import { getDisplayName } from "user/helpers/user"
import { useOrganization } from "organization/providers/OrganizationProvider"
import { isMultiChainOrganization } from "organization/helpers/organization"

type Props = {
  votes: Vote[]
  decimals?: number
  viewAllVotesLink?: string
  generateVoterLink: (address: string) => string
  addressesCount: number
  exactTotalWeightLabel: string
  totalWeightLabel: string
  displayChainLogo: boolean
  voterAddress?: string
}

const VotesCastTab: FC<Props> = ({
  votes,
  decimals,
  generateVoterLink,
  viewAllVotesLink,
  addressesCount,
  exactTotalWeightLabel,
  totalWeightLabel,
  voterAddress,
  displayChainLogo = false,
}) => {
  const { organization } = useOrganization()
  const isMultiChain = organization
    ? isMultiChainOrganization(organization)
    : false

  return (
    <Flex direction="column">
      <Flex
        align="center"
        borderBottom="gray.dark"
        justify="space-between"
        px={5}
        py={4}
      >
        <Text color="gray.600" textStyle="body.regular.md">
          {`${addressesCount} ${pluralize(
            addressesCount,
            "address",
            "addresses",
          )}`}
        </Text>
        <NumberTooltip aria-label="Total votes" label={exactTotalWeightLabel}>
          <Text color="gray.600" textStyle="body.regular.md">
            {`${totalWeightLabel} votes`}
          </Text>
        </NumberTooltip>
      </Flex>
      {votes.slice(0, 6).map((vote) => {
        const { voter, amount } = vote
        const bigWeight = BigNumber.from(amount)
        const weightLabel = getWeightLabel(bigWeight, decimals || 0)
        const exactWeightLabel = getWeightLabel(bigWeight, decimals || 0, false)
        const { address, picture } = voter
        const route = generateVoterLink(address)
        const displayName = getDisplayName(voter)
        const isOwnVote =
          voterAddress &&
          isEqual(voterAddress.toLowerCase(), address.toLowerCase())

        return (
          <Flex
            key={`proposal-ballot-cast-tab-ballot-${address}`}
            _notLast={{
              borderBottom: "gray.dark",
            }}
            align="center"
            justify="space-between"
            px={5}
            py={4}
          >
            <Stack isInline align="center" maxW="4xs" spacing={2}>
              <UserAvatar address={address} size={8} src={picture} />
              <Link href={route}>
                <AddressTooltip aria-label="Voter address" label={address}>
                  <Text fontWeight="bold" textStyle="md">
                    {isAddress(displayName)
                      ? shortString(displayName)
                      : displayName}
                  </Text>
                </AddressTooltip>
              </Link>

              {isOwnVote ? (
                <Badge colorScheme="green" fontSize="xs" ml={2}>
                  Your vote
                </Badge>
              ) : null}
            </Stack>

            <Stack align="center" direction="row" spacing={3}>
              {isMultiChain &&
              "isBridged" in vote &&
              vote.isBridged !== null &&
              !vote.isBridged ? (
                <Tag bg="yellow.100" color="yellow.900" fontSize="xs">
                  Pending
                </Tag>
              ) : null}

              <NumberTooltip aria-label="Voter votes" label={exactWeightLabel}>
                <Text color="gray.600" textStyle="body.bold.md">
                  {weightLabel}
                </Text>
              </NumberTooltip>

              {displayChainLogo && "chainId" in vote ? (
                <NetworkTag displayOnlyIcon chainId={vote.chainId} />
              ) : null}
            </Stack>
          </Flex>
        )
      })}
      {viewAllVotesLink ? (
        <Flex align="center" justify="center" mt={1} mx={2} py={2}>
          <Link className="no-underline" href={viewAllVotesLink} width="100%">
            <Button size="md" variant="tertiary" width="100%">
              View all
            </Button>
          </Link>
        </Flex>
      ) : null}
    </Flex>
  )
}

export default VotesCastTab
